import {
  emptyIncidentCriteria,
  Incident,
} from '@src/main/modules/other_incidents/domain/incident.model';
import { useIncidentDateFilter } from '@src/main/modules/other_incidents/ui/hooks/useIncidentDateFilter';
import { useIncidentFilters } from '@src/main/modules/other_incidents/ui/hooks/useIncidentFilters';
import {
  useExportExcel,
  useIncidents,
  useIncidentsRepos,
} from '@src/main/modules/other_incidents/ui/hooks/useIncidentHooks';
import {
  getAcctionsColumnProps,
  getAnsweredColumnProps,
  getAppVersionColumnProps,
  getArrivalColumnProps,
  getDepartureColumnProps,
  getDescriptionColumnProps,
  getEmployeeIdColumnProps,
  getFlightDateColumnProps,
  getFlightNumberColumnProps,
  getImageColumnProps,
  getIncidentDateColumnProps,
} from '@src/main/modules/other_incidents/ui/table/IncidentColumns';
import IncidentTable from '@src/main/modules/other_incidents/ui/table/IncidentTable';

import IncidentDetailDialog from '@src/main/modules/other_incidents/ui/components/IncidentDetailDialog';
import IncidentTableHeader from '@src/main/modules/other_incidents/ui/components/IncidentTableHeader';
import { mapIncidentFiltersToCriteria } from '@src/main/modules/other_incidents/ui/hooks/mapIncidentFiltersToCriteria';
import { useIncidentFilterHandler } from '@src/main/modules/other_incidents/ui/hooks/useIncidentFilterHandler';
import InvalidDateRangeDialog from '@src/main/shared/ui/components/calendar/InvalidDateRangeDialog';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import {
  getThreeMonthDateRange,
  toLocalDateTimeWithSeconds,
  toLocalISODate,
} from '@src/main/shared/utils/dateUtils';
import { exportToExcel } from '@src/main/shared/utils/exportToExcel';
import { Column } from 'primereact/column';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './IncidentView.module.scss';

type IncidentsViewProps = {
  formType: string;
};
type IncidentRow = {
  linkColumbus?: string;
  [key: string]: unknown;
};

const IncidentsView: FC<IncidentsViewProps> = ({ formType }) => {
  const [selectedRow, setSelectedRow] = useState<Incident | null>(null);

  const handleOpenModal = (row: IncidentRow) => {
    const incident = row as unknown as Incident;
    setSelectedRow(incident);
  };

  const handleCloseModal = () => {
    setSelectedRow(null);
  };
  const { t } = useTranslation();
  const { startDate, endDate } = useMemo(() => getThreeMonthDateRange(), []);
  const initialFilters = useIncidentFilters(startDate, endDate);
  const emptyFilters = useIncidentFilters(null, null);

  const [invalidDateModalVisible, setInvalidDateModalVisible] = useState(false);

  const incidentsHook = useIncidents(initialFilters, mapIncidentFiltersToCriteria, {
    ...emptyIncidentCriteria,
    formType,
    sortField: 'flightDate',
    sortOrder: 'desc',
  });

  const { repository } = useIncidentsRepos();
  const { mutateAsync: exportExcel, isPending: isExporting } = useExportExcel(repository);

  const {
    filters,
    setFilters,
    datos,
    total,
    isLoading,
    isFetching,
    pageNumber,
    pageSize,
    handlePageChange,
    refetchReports,
    handleSort,
    sortField,
    sortOrder,
  } = incidentsHook;

  const { handleSetFilters } = useIncidentFilterHandler(
    setFilters,
    refetchReports,
    setInvalidDateModalVisible,
  );
  const flightDateFilterTemplate = useIncidentDateFilter({
    t,
    setInvalidDateModalVisible,
  });

  const incidentDateFilterTemplate = useIncidentDateFilter({
    t,
    setInvalidDateModalVisible,
  });
  const exportCriteria = useMemo(() => {
    const base = mapIncidentFiltersToCriteria(filters, {
      ...emptyIncidentCriteria,
      formType,
    });

    return {
      ...base,
      startIncidentDate: base.startIncidentDate ? base.startIncidentDate.toISOString() : undefined,
      endIncidentDate: base.endIncidentDate ? base.endIncidentDate.toISOString() : undefined,
      sortField,
      sortOrder,
      offset: 0,
      limit: 99,
    };
  }, [filters, sortField, sortOrder, formType]);

  const handleExportExcel = async () => {
    try {
      const response = await exportExcel(exportCriteria);
      const rawData = Array.isArray(response)
        ? response
        : Object.values(response).filter((v) => typeof v === 'object');

      if (!rawData || rawData.length === 0 || rawData[0].length === 0) {
        return;
      }
      const normalizedData = rawData
        .map((item) => ({
          'Fecha Vuelo': item.flightDate ? toLocalISODate(new Date(item.flightDate)) : '',
          'Fecha Incidente': item.incidentDate
            ? toLocalDateTimeWithSeconds(new Date(item.incidentDate))
            : '',
          'Número Vuelo': item.flightNum ?? '',
          Salida: item.flightDeparture ?? '',
          Llegada: item.flightArrival ?? '',
          Matrícula: item.flightMat ?? '',
          'Empleado ID': item.employeeId ?? '',
          Nombre: item.employeeName ?? '',
          Rol: item.employeeRole ?? '',
          'Versión App': item.incidentAppVersion ?? '',
          'Versión iOS': item.incidentIosVersion ?? '',
          'Número Incidencia': item.incidentColumbusNumber ?? '',
          Captura: item.incidentImage ? 'Sí' : 'No',
          Respondido: item.fullNameAnswered ? 'Sí' : 'No',
          Categorización: item.categorization ?? '',
          Descripción: item.incidentComment ?? '',
          'Link Columbus': item.linkColumbus ?? '',
          g2L: false,
        }))
        .filter((row) =>
          Object.values(row).some(
            (v) => v !== '' && v !== 'No' && v !== false && v !== null && v !== undefined,
          ),
        );
      exportToExcel(normalizedData, 'incidents_export');
    } catch {}
  };

  const header = (
    <IncidentTableHeader
      onExportExcel={handleExportExcel}
      onRefresh={refetchReports}
      onClearFilters={() => {
        setFilters(emptyFilters);
        refetchReports();
      }}
    />
  );
  return (
    <div className={styles.container}>
      <ContentCard>
        <AppLoading isLoading={isLoading || isFetching || isExporting}>
          <div className={styles['header-row']}>
            <h2 className={styles.title}>{t('otherIncidentManagement.title')}</h2>
          </div>
          <IncidentTable<Incident>
            data={datos}
            filters={filters}
            setFilters={handleSetFilters}
            header={header}
            loading={isLoading}
            dataKey="id"
            globalFilterFields={[
              'incidentDate',
              'flightNumber',
              'employeeId',
              'departure',
              'arrival',
              'state',
              'comment',
            ]}
            totalRecords={total}
            first={pageNumber * pageSize}
            rows={pageSize}
            onPage={handlePageChange}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
          >
            <Column {...getFlightDateColumnProps(t, flightDateFilterTemplate)} />
            <Column {...getIncidentDateColumnProps(t, incidentDateFilterTemplate)} />
            <Column {...getFlightNumberColumnProps(t)} />
            <Column {...getDepartureColumnProps(t)} />
            <Column {...getArrivalColumnProps(t)} />
            <Column {...getEmployeeIdColumnProps(t)} />
            <Column {...getAppVersionColumnProps(t)} />
            <Column {...getImageColumnProps(t)} />
            <Column {...getDescriptionColumnProps(t)} />
            <Column {...getAnsweredColumnProps(t)} />
            <Column {...getAcctionsColumnProps(t, handleOpenModal)} />
          </IncidentTable>

          <InvalidDateRangeDialog
            invalidDateRangeModalVisible={invalidDateModalVisible}
            setInvalidDateRangeModalVisible={setInvalidDateModalVisible}
            t={t}
          />
        </AppLoading>
      </ContentCard>
      <IncidentDetailDialog onHide={handleCloseModal} incident={selectedRow} />
    </div>
  );
};

export default IncidentsView;
